import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { ForecastService } from '@app/api/forecasting';
import { useError } from '@app/hooks/useError';
import { BaseForecastModelWithAvailability } from '@app/common/static-data/models';
import { useDemo } from '@app/components/demo/DemoProvider';

interface UseAvailableInitTimesProps {
  availableModels: BaseForecastModelWithAvailability[];
}

export function useAvailableInitTimes({ availableModels }: UseAvailableInitTimesProps) {
  const { showError } = useError();
  const { isDemo } = useDemo();

  // Init time state for Jua models
  const [selectedInitTimes, setSelectedInitTimes] = useState<Record<string, string>>({});

  // Available init times cache for Jua models
  const [availableInitTimes, setAvailableInitTimes] = useState<Record<string, string[]>>({});
  const [loadingInitTimes, setLoadingInitTimes] = useState<Record<string, boolean>>({});
  const [openInitTimeSelectors, setOpenInitTimeSelectors] = useState<Record<string, boolean>>({});

  // Track which models we've already initiated fetching for
  const fetchedModelsRef = useRef<Set<string>>(new Set());

  // Memoize Jua models and other models with init time selection to prevent unnecessary re-fetching
  const juaModels = useMemo(() => {
    return availableModels.filter(
      (model) =>
        model.provider === 'jua' ||
        model.id === 'ecmwf_ifs_single' ||
        model.id === 'noaa_gfs_single',
    );
  }, [availableModels]);

  // Function to fetch available init times for a specific Jua model
  const fetchAvailableInitTimes = useCallback(
    async (modelId: string) => {
      // Check if we already have data, are loading, or have already initiated fetch
      if (
        availableInitTimes[modelId] ||
        loadingInitTimes[modelId] ||
        fetchedModelsRef.current.has(modelId)
      ) {
        return;
      }

      // Mark this model as being fetched to prevent duplicate calls
      fetchedModelsRef.current.add(modelId);
      setLoadingInitTimes((prev) => ({ ...prev, [modelId]: true }));

      try {
        const initTimes = await ForecastService.getAvailableInitTimes(modelId, isDemo);

        // Filter to only keep init times less than 36 hours old
        const now = new Date();
        const oldestAvailableTime = new Date(now.getTime() - 36 * 60 * 60 * 1000);

        const filteredInitTimes = initTimes.filter((initTime) => {
          try {
            const initDate = new Date(initTime);
            return isDemo || initDate >= oldestAvailableTime;
          } catch (error) {
            // If we can't parse the date, exclude it from the list
            console.warn(`Failed to parse init time: ${initTime}`, error);
            return false;
          }
        });

        setAvailableInitTimes((prev) => ({
          ...prev,
          [modelId]: filteredInitTimes,
        }));
      } catch (error) {
        console.error(`Failed to fetch available init times for ${modelId}:`, error);
        // Remove from fetched set on error so we can retry
        fetchedModelsRef.current.delete(modelId);
        showError({
          code: 'INIT_TIMES_FETCH_ERROR',
          message: `Failed to fetch available init times for ${modelId}`,
          severity: 'error',
          details: { modelId },
          suggestion: 'Please try again later or contact support if the issue persists.',
        });
      } finally {
        setLoadingInitTimes((prev) => ({ ...prev, [modelId]: false }));
      }
    },
    [availableInitTimes, loadingInitTimes, showError],
  );

  const handleInitTimeSelectorToggle = (modelId: string) => {
    // Toggle the open state of the init time selector for the given model
    setOpenInitTimeSelectors((prev) => ({
      ...prev,
      [modelId]: !prev[modelId],
    }));

    // Fetch available init times for the given model (if not already fetched)
    fetchAvailableInitTimes(modelId);
  };

  // Handle init time changes
  const handleInitTimeChange = useCallback((modelId: string, initTime: string) => {
    setSelectedInitTimes((prev) => ({
      ...prev,
      [modelId]: initTime,
    }));
    setOpenInitTimeSelectors((prev) => ({
      ...prev,
      [modelId]: false,
    }));
  }, []);

  // Initialize selected init times for Jua models
  useEffect(() => {
    if (juaModels.length === 0) return;

    setSelectedInitTimes((prev) => {
      const newInitTimes = { ...prev };
      let hasChanges = false;

      juaModels.forEach((model) => {
        if (!newInitTimes[model.id]) {
          newInitTimes[model.id] = 'live';
          hasChanges = true;
        }
      });

      return hasChanges ? newInitTimes : prev;
    });
  }, [juaModels]);

  return {
    availableInitTimes,
    loadingInitTimes,
    openInitTimeSelectors,
    selectedInitTimes,
    handleInitTimeChange,
    handleInitTimeSelectorToggle,
  };
}
